import { useParams } from 'react-router-dom';
import { useModel, useModels } from '../../../lib/supabase/models/supabaseModelService';
import { useModelFormat, useCanEditFormat, useUpdateModelFormat } from '../../../lib/supabase/formats/supabaseModelFormatService';
import { useCanEditResetTimer, useModelResetTimer } from '../../../lib/supabase/formats/supabaseModelResetTimesService';
import { useRegenerateModelLists, useDailyModelListForModel } from '../../../lib/supabase/formats/supabaseModelDailyFormatService';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { LoadingState } from '../../ui/loading-state';
import { Breadcrumbs } from '../../ui/breadcrumbs';
import { toast } from 'react-hot-toast';
import { Button } from '../../ui/button';
import { ModelFormatItem } from '@shiftly/shared/src/schema';
import { useState, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { BulkEditDialog } from './BulkEditDialog';
import { FormatDecoder } from './FormatDecoder';
import { FormatList } from './FormatList';
import { ResetTimeDialog } from './ResetTimeDialog';
import { ConfirmDialog } from '../../ui/dialogs/confirm-dialog';
import { Checkbox } from '../../ui/checkbox';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { Skeleton } from '../../ui/skeleton';
import { useAuth } from '../../../contexts/AuthContext';
import { getTimeZones } from '@vvo/tzdb';
import { Label } from '../../ui/label';

const ModelScheduleView = () => {
  const { id } = useParams<{ id: string }>();
  const { user, permissions } = useAuth();
  const { data: model, isLoading: modelLoading } = useModel(id || '');
  const { data: models = [] } = useModels();
  const { data: modelFormat, isLoading: formatLoading } = useModelFormat(id || '');
  const { mutateAsync: updateFormat } = useUpdateModelFormat();
  const { data: resetTimer, isLoading: resetTimerLoading } = useModelResetTimer(id || '');
  const canEdit = useCanEditFormat();
  const canViewResetTime = useCanEditResetTimer(user?.id || '', permissions, id || '');

  // Get current time in BST
  const nowBST = DateTime.now().setZone('Europe/London');

  // Determine how many days to show based on time
  const shouldShowThirdDay = nowBST.hour >= 18; // Past 6pm BST
  const numberOfDays = shouldShowThirdDay ? 3 : 2;

  const today = useMemo(() => { return DateTime.utc().setZone('Europe/London') }, []);
  const { mutateAsync: regenerateModelLists, isPending: isGenerating } = useRegenerateModelLists();

  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkFormatText, setBulkFormatText] = useState('');
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [includeToday, setIncludeToday] = useState(false);
  const [showResetTimeDialog, setShowResetTimeDialog] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    return localStorage.getItem('shiftly-timezone') || 'Europe/London';
  });

  // Create date range array based on current time
  const dateRange = Array.from({ length: numberOfDays }, (_, index) => {
    const date = today.plus({ days: index });
    return date;
  });

  // Get the current date for display (index 0 = today, 1 = tomorrow, 2 = day after if past 6pm)
  const currentDate = dateRange[selectedDayIndex];

  // Fetch slots for the current selected date
  const { data: currentSlots, isLoading: slotsLoading } = useDailyModelListForModel(id || '', currentDate);

  // When opening the bulk edit dialog, populate it with current template
  useEffect(() => {
    if (isBulkEditing && modelFormat?.items && modelFormat.items.length > 0) {
      const formattedItems = FormatDecoder.encodeFormatItems(modelFormat.items);
      setBulkFormatText(formattedItems);
    }
  }, [isBulkEditing, modelFormat?.items]);

  const parseFormatText = (text: string): ModelFormatItem[] => {
    try {
      const items = FormatDecoder.decodeFormatText(text);
      return items;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to parse format text');
      return [];
    }
  };

  const handleSaveBulkEdit = async () => {
    try {
      const items = parseFormatText(bulkFormatText);
      if (items.length === 0) {
        toast.error('No valid format items found');
        return;
      }

      await updateFormat({
        model_id: id || '',
        format: { items }
      });
      setIsBulkEditing(false);
      setBulkFormatText('');
      toast.success('Format template updated successfully');
    } catch (error) {
      toast.error('Failed to update format template');
    }
  };

  const handleRegenerateModelLists = async () => {
    if (!id) return;

    try {
      const result = await regenerateModelLists({ model_id: id, includeToday });
      if (result.generatedCount > 0) {
        toast.success(`Generated ${result.generatedCount} daily model lists`);
      } else {
        toast.success('All daily model lists are already up to date');
      }
      setShowGenerateDialog(false);
      setIncludeToday(false);
    } catch (error) {
      toast.error('Failed to generate model lists');
    }
  };

  const handleDialogClose = () => {
    setShowGenerateDialog(false);
    setIncludeToday(false);
  };

  const handleTimezoneChange = (timezone: string) => {
    setSelectedTimezone(timezone);
    localStorage.setItem('shiftly-timezone', timezone);
  };

  const TIMEZONE_OPTIONS = getTimeZones().map(tz => ({
    label: `${tz.name}`,
    rawOffsetInMinutes: tz.rawOffsetInMinutes,
    value: tz.name
  }));

  // Calculate timezone offset in hours from BST using Luxon
  const getTimezoneOffsetFromBST = (selectedTimezone: string): number => {
    if (selectedTimezone === 'Europe/London') {
      return 0;
    }

    const selectedTz = TIMEZONE_OPTIONS.find(tz => tz.value === selectedTimezone);
    const londonTz = TIMEZONE_OPTIONS.find(tz => tz.value === 'Europe/London');

    if (!selectedTz || !londonTz) {
      return 0;
    }

    const offsetMinutes = selectedTz.rawOffsetInMinutes - londonTz.rawOffsetInMinutes;
    return Math.round(offsetMinutes / 60);
  };

  const timezoneOffsetHours = getTimezoneOffsetFromBST(selectedTimezone);

  // Create date range for display using BST
  const getDateRange = () => {
    const todayBST = nowBST.startOf('day');
    const endDateBST = todayBST.plus({ days: shouldShowThirdDay ? 2 : 1 }); // Show appropriate range

    const startDateBST = includeToday ? todayBST : todayBST.plus({ days: 1 });

    return {
      startDate: startDateBST.toJSDate(),
      endDate: endDateBST.toJSDate()
    };
  };

  const { startDate, endDate } = getDateRange();

  const dialogMessage = (
    <div className="space-y-4">
      <div className="text-sm text-foreground dark:text-muted-foreground">
        This will regenerate all lists from <strong>{format(startDate, 'MMM d, yyyy')}</strong> to{' '}
        <strong>{format(endDate, 'MMM d, yyyy')}</strong> for <strong>{model?.name}</strong>.
      </div>

      <div className="space-y-3">
        <div className="flex items-center space-x-3">
          <Checkbox
            id="include-today"
            checked={includeToday}
            onCheckedChange={(checked) => setIncludeToday(checked === true)}
          />
          <Label
            htmlFor="include-today"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Include today ({nowBST.toFormat('MMM d, yyyy')})
          </Label>
        </div>
      </div>
    </div>
  );

  if (modelLoading || formatLoading || slotsLoading) {
    return <LoadingState />;
  }

  if (!model) {
    return <div>Model not found</div>;
  }

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Models', href: '/models' },
    { title: model.name, href: `/models/${model.id}` },
    { title: 'Schedule' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-1.5 pb-4 border-b border-brand-border">
        <Breadcrumbs items={breadcrumbs} className="mb-2" />
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
              {model.name} Schedule
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground">
              View daily model lists and manage format templates
            </p>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button
                onClick={() => setShowGenerateDialog(true)}
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Generate Lists'}
              </Button>
              <Button onClick={() => setIsBulkEditing(true)}>
                Edit Format Template
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Resolved Format with Model Names */}
      {modelFormat?.items && modelFormat.items.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  Format Schedule (Group {model.scheduling_group})
                </CardTitle>
                {canViewResetTime && (
                  <div className="mt-1">
                    <p
                      className={`text-sm text-muted-foreground dark:text-muted-foreground ${canEdit ? 'cursor-pointer hover:text-foreground dark:hover:text-zinc-200 hover:underline' : ''}`}
                      onClick={canEdit ? () => setShowResetTimeDialog(true) : undefined}
                    >
                      {resetTimerLoading ? (
                        <Skeleton className="h-3 w-16 inline-block" />
                      ) : resetTimer ? (
                        `Reset Time: ${resetTimer.time.setZone('Europe/London').toFormat('HH:mm:ss')} (BST)`
                      ) : (
                        'No reset time set'
                      )}
                    </p>
                  </div>
                )}
              </div>
              {/* Day Navigation */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDayIndex(Math.max(0, selectedDayIndex - 1))}
                    disabled={selectedDayIndex === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDayIndex(Math.min(dateRange.length - 1, selectedDayIndex + 1))}
                    disabled={selectedDayIndex === dateRange.length - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDayIndex(0)}
                  >
                    Today
                  </Button>
                </div>

                <h2 className="text-lg font-bold">
                  {currentDate.toFormat('MMM d, yyyy')}
                </h2>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-4">
            <FormatList
              slots={currentSlots}
              models={models}
              currentModel={model}
              isLoading={slotsLoading}
              canEdit={canEdit}
              selectedTimezone={selectedTimezone}
              timezoneOffsetHours={timezoneOffsetHours}
              date={currentDate}
              onTimezoneChange={handleTimezoneChange}
              timezoneOptions={TIMEZONE_OPTIONS}
            />
          </CardContent>
        </Card>
      )}

      {/* Format Template Editing */}
      <BulkEditDialog
        open={isBulkEditing}
        onOpenChange={setIsBulkEditing}
        value={bulkFormatText}
        onChange={setBulkFormatText}
        onSave={handleSaveBulkEdit}
        hasExistingItems={(modelFormat?.items?.length || 0) > 0}
      />

      {/* Generate Lists Confirmation */}
      <ConfirmDialog
        isOpen={showGenerateDialog}
        onClose={handleDialogClose}
        onConfirm={handleRegenerateModelLists}
        title="Generate Model Lists"
        message={dialogMessage}
        confirmText="Generate Lists"
        cancelText="Cancel"
        loading={isGenerating}
      />

      {/* Reset Time Dialog */}
      <ResetTimeDialog
        open={showResetTimeDialog}
        onOpenChange={setShowResetTimeDialog}
        model_id={id || ''}
        canEdit={canEdit}
      />
    </div>
  );
};

export default ModelScheduleView; 